import type { RequestContext } from "../context/request-context.js";
import type { RdoRepository } from "../../repositories/operational-repositories.js";
import type { AdminRepository } from "../../administration/admin-domain.js";
import type { IdentityRepository } from "../../infrastructure/database/memory/in-memory-identity-repositories.js";
import type { AuditService } from "./audit-service.js";
import {
  type Rdo,
  type RdoConsolidated,
  type RdoFilterOptions,
  type EnrichedRdo,
  type CollaboratorRdoGroup,
  validateRdoHours
} from "../../domain/rdo/rdo-domain.js";
import {
  buildConsolidatedFileName,
  buildConsolidatedModel,
  filterEntriesForConsolidation,
  validateConsolidationFilters,
  EMPTY_FIELD,
  NO_ENTRIES_MESSAGE,
  type RdoConsolidatedHeader,
  type RdoConsolidatedModel,
  type RdoConsolidationFilters
} from "../../domain/rdo/rdo-consolidation.js";
import {
  appendEntryChangeHistory,
  diffRdoEntry,
  resolveEntryEditability,
  ENTRY_FROZEN_MESSAGE,
  type RdoEntryChangeRecord,
  type EntryEditability
} from "../../domain/rdo/rdo-entry-editing.js";
import {
  assertSubOsLinkedToProfessional,
  buildWorkOrderTree,
  isProjectLinkedToWorkOrder,
  projectsForWorkOrder,
  resolveSubOsContext,
  subOsForProfessional,
  PROJECT_NOT_LINKED_MESSAGE,
  SUB_OS_REQUIRED_MESSAGE,
  type ProjectLike,
  type SubOsContext,
  type SubOsLike,
  type UnitAreaLike,
  type WorkOrderProjectLinkLike,
  type WorkOrderTreeNode
} from "../../domain/rdo/sub-os-context.js";
import {
  workOrderBelongsToClient,
  WORK_ORDER_CLIENT_MISMATCH_MESSAGE
} from "../../domain/project/project-work-order-cascade.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { domainId, type RdoId, type RdoConsolidatedId, type WorkOrderId, type ServiceFrontId, type ProfessionalId, type ClientId } from "../../shared/kernel/identifiers.js";
import type { UtcDateTime } from "../../shared/kernel/date-time.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/**
 * Rótulos para cadastro que não existe mais. Um id interno na tela não diz
 * nada ao usuário; estes rótulos dizem, e o apontamento continua editável para
 * que ele aponte um cadastro válido.
 */
export const UNRESOLVED_PROFESSIONAL = 'Profissional não encontrado (registro histórico)';
export const UNRESOLVED_LABEL = 'Não encontrado (registro histórico)';

/** "yyyy-mm-dd" → "dd/mm/yyyy" sem passar por Date(), para não deslocar o dia. */
function formatDateBr(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? "");
  return match ? `${match[3]}/${match[2]}/${match[1]}` : isoDate;
}

export interface CreateRdoRequest {
  clientId?: string;
  workOrderId: string;
  subWorkOrderId?: string | null;
  unitAreaId?: string | null;
  serviceFrontId?: string | null;
  professionalId: string;
  date: string;
  startTime: string;
  endTime: string;
  normalHours: number;
  overtimeHours: number;
  om?: string | null;
  functionName?: string;
  shift?: string;
  description?: string;
  /** Resolvido a partir da Frente quando não informado. */
  projectId?: string | null;
  inspectorComment?: string | null;
}

export interface UpdateRdoRequest extends CreateRdoRequest {
  status?: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'ISSUED' | 'CANCELLED';
}

/** Resolve o Projeto para o cabeçalho, sem acoplar o RDO ao repositório de projetos. */
export type ProjectLookup = (projectId: string) => Promise<{ code: string; name: string } | null>;
/** Carregadores da hierarquia, para o RDO não depender do repositório de projetos. */
export type ProjectLoader = (clientId: string | null) => Promise<ProjectLike[]>;
export type UnitAreaLoader = (clientId: string | null) => Promise<UnitAreaLike[]>;

export class RdoService {
  constructor(
    private rdoRepo: RdoRepository,
    private adminRepo?: AdminRepository,
    private identityRepo?: IdentityRepository,
    private audit?: AuditService,
    private projectLookup?: ProjectLookup,
    private projectLoader?: ProjectLoader,
    private unitAreaLoader?: UnitAreaLoader
  ) {}

  private getClientId(context: RequestContext): ClientId {
    if (!context.activeClientId) {
      throw new AppError(ErrorCode.Authorization, 'Nenhum cliente ativo no contexto de requisição');
    }
    return context.activeClientId;
  }

  /** Projeto da Frente — vem do cadastro da Frente, ou da OS dela quando legada. */
  private async resolveProjectIdFromFront(clientId: ClientId, serviceFrontId?: string | null): Promise<string | null> {
    if (!serviceFrontId || !this.adminRepo) return null;
    const fronts = await this.adminRepo.getServiceFronts(clientId);
    const front = fronts.find(f => f.id === serviceFrontId);
    if (!front) return null;
    if (front.projectId) return front.projectId;
    if (!front.workOrderId) return null;
    const workOrders = await this.adminRepo.getWorkOrders(clientId);
    return workOrders.find(wo => wo.id === front.workOrderId)?.projectId ?? null;
  }

  // =========================================================================
  // Hierarquia OS → Projeto → Unidade/Área → Sub-OS
  // =========================================================================

  /** Entidades da hierarquia, carregadas uma vez por consulta. */
  private async loadHierarchy(context: RequestContext, clientId?: string | null) {
    const scope = (clientId ?? undefined) as any;
    const subOrders = this.adminRepo ? await this.adminRepo.getSubWorkOrders(scope) : [];
    const workOrders = this.adminRepo ? await this.adminRepo.getWorkOrders(scope) : [];
    const accesses = this.adminRepo?.getProfessionalSubOsAccesses
      ? await this.adminRepo.getProfessionalSubOsAccesses(scope)
      : [];
    const links = this.adminRepo?.getWorkOrderProjectLinks
      ? await this.adminRepo.getWorkOrderProjectLinks(scope)
      : [];
    const projects = this.projectLoader ? await this.projectLoader(clientId ?? null) : [];
    const unitAreas = this.unitAreaLoader ? await this.unitAreaLoader(clientId ?? null) : [];

    return { subOrders, workOrders, accesses, links, projects, unitAreas };
  }

  /** Projetos vinculados a uma OS — uma OS possui vários. */
  async listProjectsForWorkOrder(context: RequestContext, workOrderId: string): Promise<ProjectLike[]> {
    const clientId = this.lookupClientId(context);
    const { workOrders, projects, links } = await this.loadHierarchy(context, clientId);
    const workOrder = (workOrders as any[]).find(wo => wo.id === workOrderId) ?? null;
    return projectsForWorkOrder(workOrder, projects as any, links as any);
  }

  /**
   * Vínculos OS ↔ Projeto no escopo do Cliente ativo.
   *
   * Leitura para a tela montar a cascata e o modal de vínculo sem alcançar
   * o repositório por fora do serviço.
   */
  async listWorkOrderProjectLinks(context: RequestContext): Promise<WorkOrderProjectLinkLike[]> {
    const clientId = this.lookupClientId(context);
    const { links } = await this.loadHierarchy(context, clientId);
    return links as any;
  }

  /**
   * A OS escolhida pertence mesmo ao Cliente informado?
   *
   * Existe para ser chamada ANTES de criar o Projeto: descobrir a
   * inconsistência depois deixaria um Projeto gravado sem a OS que o motivou.
   */
  async assertWorkOrderBelongsToClient(
    context: RequestContext,
    workOrderId: string,
    clientId: string
  ): Promise<void> {
    if (!clientId) {
      throw new AppError(ErrorCode.Validation, 'Client is required', {
        safeMessage: 'Selecione o Cliente.'
      });
    }

    const workOrders = this.adminRepo ? await this.adminRepo.getWorkOrders() : [];
    const workOrder = (workOrders as any[]).find(wo => wo.id === workOrderId) ?? null;
    if (!workOrder) {
      throw new AppError(ErrorCode.NotFound, `Work order ${workOrderId} not found`, {
        safeMessage: 'Ordem de Serviço não encontrada.'
      });
    }
    if (!workOrderBelongsToClient(workOrder, clientId)) {
      throw new AppError(ErrorCode.Validation, 'Work order does not belong to the given client', {
        safeMessage: WORK_ORDER_CLIENT_MISMATCH_MESSAGE
      });
    }
  }

  /**
   * Vincula um Projeto existente a uma OS. A OS apenas VINCULA Projetos —
   * o cadastro do Projeto continua sendo entidade própria.
   */
  async linkProjectToWorkOrder(context: RequestContext, workOrderId: string, projectId: string): Promise<void> {
    new RbacAuthorizationService().require(context, PermissionCode.AdministrationManage);
    if (!this.adminRepo?.saveWorkOrderProjectLink) {
      throw new AppError(ErrorCode.NotFound, 'Work order project links not supported', {
        safeMessage: 'Vínculo de Projeto à OS indisponível nesta sessão.'
      });
    }

    const clientId = this.lookupClientId(context);
    const { workOrders, projects, links } = await this.loadHierarchy(context, clientId);

    const workOrder = (workOrders as any[]).find(wo => wo.id === workOrderId);
    if (!workOrder) {
      throw new AppError(ErrorCode.NotFound, `Work order ${workOrderId} not found`, {
        safeMessage: 'Ordem de Serviço não encontrada.'
      });
    }
    const project = (projects as any[]).find(p => p.id === projectId);
    if (!project) {
      throw new AppError(ErrorCode.NotFound, `Project ${projectId} not found`, {
        safeMessage: 'Projeto não encontrado.'
      });
    }

    // OS de um Cliente nunca recebe Projeto de outro, mesmo que a tela envie o
    // par adulterado — a consulta pode alcançar os dois quando não há Cliente
    // no contexto, e nesse caso só esta checagem impede a combinação.
    if (workOrder.clientId && project.clientId && workOrder.clientId !== project.clientId) {
      throw new AppError(ErrorCode.Validation, 'Work order does not belong to the project client', {
        safeMessage: WORK_ORDER_CLIENT_MISMATCH_MESSAGE
      });
    }

    // Vincular duas vezes o mesmo Projeto não cria duplicata.
    if ((links as any[]).some(l => l.workOrderId === workOrderId && l.projectId === projectId)) return;

    await this.adminRepo.saveWorkOrderProjectLink({
      id: `wopl-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`,
      clientId: (workOrder.clientId ?? project.clientId) as any,
      workOrderId: workOrderId as any,
      projectId,
      createdAt: new Date().toISOString(),
      createdBy: context.userId || null
    });
  }

  async unlinkProjectFromWorkOrder(context: RequestContext, workOrderId: string, projectId: string): Promise<void> {
    new RbacAuthorizationService().require(context, PermissionCode.AdministrationManage);
    if (!this.adminRepo?.deleteWorkOrderProjectLink || !this.adminRepo.getWorkOrderProjectLinks) return;

    const clientId = this.lookupClientId(context);
    const { subOrders, links, workOrders } = await this.loadHierarchy(context, clientId);

    // Projeto com Sub-OS naquela OS não é desvinculado: isso deixaria a Sub-OS
    // órfã do seu Projeto.
    const inUse = (subOrders as any[]).some(sub => sub.workOrderId === workOrderId && sub.projectId === projectId);
    if (inUse) {
      throw new AppError(ErrorCode.Conflict, 'Project has sub work orders in this work order', {
        safeMessage: 'Este Projeto possui Sub-OS nesta Ordem de Serviço e não pode ser desvinculado.'
      });
    }

    for (const link of (links as any[]).filter(l => l.workOrderId === workOrderId && l.projectId === projectId)) {
      await this.adminRepo.deleteWorkOrderProjectLink(link.id);
    }

    // O campo legado workOrder.projectId também conta como vínculo. Apagar só
    // a entidade de vínculo deixaria o Projeto ainda pendurado nesta OS —
    // limpar o campo obsoleto é sair dele, não voltar a usá-lo como relação.
    const workOrder = (workOrders as any[]).find(wo => wo.id === workOrderId);
    if (workOrder?.projectId === projectId && this.adminRepo.saveWorkOrder) {
      await this.adminRepo.saveWorkOrder({
        ...workOrder,
        projectId: '',
        updatedAt: new Date().toISOString()
      });
    }
  }

  /**
   * Valida o contexto de uma Sub-OS antes de gravá-la: a OS existe, o Projeto
   * existe, e o Projeto está vinculado àquela OS.
   */
  async assertSubOsContextIsValid(
    context: RequestContext,
    input: { workOrderId: string; projectId: string; unitAreaId?: string | null }
  ): Promise<void> {
    const clientId = this.lookupClientId(context);
    const { workOrders, projects, links, unitAreas } = await this.loadHierarchy(context, clientId);

    const workOrder = (workOrders as any[]).find(wo => wo.id === input.workOrderId) ?? null;
    if (!workOrder) {
      throw new AppError(ErrorCode.NotFound, `Work order ${input.workOrderId} not found`, {
        safeMessage: 'Ordem de Serviço não encontrada.'
      });
    }
    if (!(projects as any[]).some(p => p.id === input.projectId)) {
      throw new AppError(ErrorCode.NotFound, `Project ${input.projectId} not found`, {
        safeMessage: 'Projeto não encontrado.'
      });
    }
    if (!isProjectLinkedToWorkOrder(workOrder, input.projectId, links as any)) {
      throw new AppError(ErrorCode.Validation, 'Project not linked to work order', {
        safeMessage: PROJECT_NOT_LINKED_MESSAGE
      });
    }
    if (input.unitAreaId) {
      const belongs = (unitAreas as any[]).some(a => a.id === input.unitAreaId && a.projectId === input.projectId);
      if (!belongs) {
        throw new AppError(ErrorCode.Validation, 'Unit area does not belong to the project', {
          safeMessage: 'A Unidade/Área selecionada não pertence ao Projeto informado.'
        });
      }
    }
  }

  /** Árvore OS → Projetos → Sub-OS da tela de Gestão. */
  async buildWorkOrderTree(context: RequestContext): Promise<WorkOrderTreeNode[]> {
    const clientId = this.lookupClientId(context);
    const { workOrders, projects, links, subOrders } = await this.loadHierarchy(context, clientId);
    return buildWorkOrderTree({
      workOrders: workOrders as any,
      projects: projects as any,
      links: links as any,
      subOrders: subOrders as any
    });
  }

  /** Cliente para a consulta dos cadastros — o sentinela da tela nunca é um cliente. */
  private lookupClientId(context: RequestContext): string | null {
    const activeClientId = context.activeClientId ? String(context.activeClientId) : "";
    if (!activeClientId || activeClientId === "cli-default") return null;
    return activeClientId;
  }

  /**
   * Sub-OS que o colaborador pode apontar, cada uma já com OS, Projeto,
   * Cliente e Unidade/Área resolvidos. É o único seletor do RDO além do
   * próprio colaborador.
   */
  async listSubOsForProfessional(
    context: RequestContext,
    professionalId: string
  ): Promise<{ subOs: SubOsLike; context: SubOsContext }[]> {
    if (!professionalId) return [];

    const clientId = this.lookupClientId(context);
    const { subOrders, workOrders, accesses, projects, unitAreas, links } = await this.loadHierarchy(context, clientId);
    const clientNames = await this.loadClientNames(projects);

    return subOsForProfessional(subOrders as any, accesses as any, professionalId)
      .filter(subOs => !clientId || subOs.clientId === clientId)
      .map(subOs => ({
        subOs,
        context: resolveSubOsContext({
          subOs,
          workOrders: workOrders as any,
          projects: projects as any,
          unitAreas: unitAreas as any,
          links: links as any,
          clientNames
        })
      }))
      .filter(item => item.context.clientId === item.subOs.clientId);
  }

  /** Cadeia completa de UMA Sub-OS, para os campos automáticos do formulário. */
  async resolveSubOsContext(context: RequestContext, subOsId: string): Promise<SubOsContext> {
    const clientId = this.lookupClientId(context);
    const { subOrders, workOrders, projects, unitAreas, links } = await this.loadHierarchy(context, clientId);
    const subOs = (subOrders as any[]).find(s => s.id === subOsId) ?? null;
    const clientNames = await this.loadClientNames(projects);

    return resolveSubOsContext({
      subOs,
      workOrders: workOrders as any,
      projects: projects as any,
      unitAreas: unitAreas as any,
      links: links as any,
      clientNames
    });
  }

  private async loadClientNames(projects: readonly { clientId: string }[]): Promise<Map<string, string>> {
    const names = new Map<string, string>();
    if (!this.identityRepo) return names;

    for (const clientId of new Set(projects.map(p => String(p.clientId)).filter(Boolean))) {
      const client = await this.identityRepo.getClient(clientId as any);
      if (client) names.set(clientId, client.name || client.legalName || "Cliente");
    }
    return names;
  }

  /**
   * Revalida o vínculo Sub-OS ↔ colaborador e devolve a cadeia resolvida.
   * Chamado antes de gravar: o que a tela enviou nunca é aceito de olhos
   * fechados.
   */
  private async requireSubOsContext(
    context: RequestContext,
    professionalId: string,
    subOsId: string | null | undefined
  ): Promise<SubOsContext | null> {
    // Sem o cadastro de OS/Sub-OS disponível não há o que resolver nem o que
    // validar — a hierarquia simplesmente não existe nessa composição.
    if (!this.adminRepo) return null;

    if (!subOsId) {
      throw new AppError(ErrorCode.Validation, "Sub work order is required", { safeMessage: SUB_OS_REQUIRED_MESSAGE });
    }

    const clientId = this.lookupClientId(context);
    const { subOrders, workOrders, accesses, projects, unitAreas, links } = await this.loadHierarchy(context, clientId);

    assertSubOsLinkedToProfessional(subOrders as any, accesses as any, professionalId, subOsId);

    const subOs = (subOrders as any[]).find(s => s.id === subOsId) ?? null;
    const workOrder = (workOrders as any[]).find(w => w.id === subOs?.workOrderId) ?? null;
    // O resolvedor pode inferir Cliente pelo Projeto; isso NÃO pode esconder
    // um vínculo físico Sub-OS → Cliente/OS divergente no banco.
    if (!subOs || !workOrder || !clientId ||
        subOs.clientId !== clientId || workOrder.clientId !== clientId) {
      throw new AppError(ErrorCode.Authorization, 'RDO sub-order/client mismatch', {
        safeMessage: 'A Sub-OS não pertence à OS e ao Cliente selecionados.'
      });
    }
    return resolveSubOsContext({
      subOs,
      workOrders: workOrders as any,
      projects: projects as any,
      unitAreas: unitAreas as any,
      links: links as any
    });
  }

  /**
   * A UI não é fronteira de segurança: valide a cadeia real também no serviço.
   * A validação fica restrita às composições com AdminRepository real.
   */
  private async assertRdoOperationalHierarchy(
    context: RequestContext,
    clientId: ClientId,
    req: Pick<CreateRdoRequest, 'workOrderId' | 'projectId' | 'serviceFrontId' | 'unitAreaId'>,
    subOsContext: SubOsContext | null
  ): Promise<void> {
    if (!this.adminRepo || !subOsContext) return;
    // Mesmo gate multicliente do AdminService: acesso revogado bloqueia o RDO.
    if (this.identityRepo && context.userId &&
        !context.permissions?.has(PermissionCode.AdministrationManage)) {
      const accesses = await this.identityRepo.listAccesses(context.userId);
      if (accesses.length > 0 && !accesses.some(a =>
        a.clientId === clientId && a.status === 'ACTIVE' && a.deletedAt === null)) {
        throw new AppError(ErrorCode.Authorization, 'RDO client access denied', {
          safeMessage: 'Você não possui autorização para registrar RDO neste Cliente.'
        });
      }
    }
    // Mantém integração legada: se o chamador não enviou Projeto, usa a
    // associação inequívoca comprovada pela Sub-OS, nunca um fallback fictício.
    const projectId = req.projectId ?? subOsContext.projectId ?? '';
    if (!subOsContext.clientId || subOsContext.clientId !== clientId ||
        !subOsContext.workOrderId || subOsContext.workOrderId !== req.workOrderId ||
        !subOsContext.projectId || subOsContext.projectId !== projectId) {
      throw new AppError(ErrorCode.Validation, 'RDO hierarchy mismatch', {
        safeMessage: 'Cliente, Projeto, OS e Sub-OS não correspondem ao mesmo contexto.'
      });
    }
    const { workOrders, projects, links, unitAreas } = await this.loadHierarchy(context, clientId);
    const wo = workOrders.find(w => w.id === req.workOrderId);
    const project = projects.find(p => p.id === req.projectId);
    if (!wo || !project || wo.clientId !== clientId || project.clientId !== clientId ||
        !isProjectLinkedToWorkOrder(wo as any, projectId, links as any)) {
      throw new AppError(ErrorCode.Validation, 'RDO Project/OS mismatch', {
        safeMessage: 'A OS selecionada não está vinculada ao Projeto deste Cliente.'
      });
    }
    const fronts = await this.adminRepo.getServiceFronts(clientId);
    const front = fronts.find(f => f.id === req.serviceFrontId);
    if (!front || front.clientId !== clientId ||
        (front.status && front.status !== 'ACTIVE')) {
      throw new AppError(ErrorCode.Validation, 'RDO service front invalid', {
        safeMessage: 'Frente de Serviço inexistente, inativa ou pertencente a outro Cliente.'
      });
    }
    const frontProjectId = front.projectId ?? (
      front.workOrderId ? workOrders.find(w => w.id === front.workOrderId)?.projectId ?? null : null
    );
    if (frontProjectId !== projectId) {
      throw new AppError(ErrorCode.Validation, 'RDO front/project mismatch', {
        safeMessage: 'A Frente de Serviço não pertence ao Projeto selecionado.'
      });
    }
    if (req.unitAreaId && subOsContext.unitAreaId && req.unitAreaId !== subOsContext.unitAreaId) {
      throw new AppError(ErrorCode.Validation, 'RDO sub-order unit mismatch', {
        safeMessage: 'A Unidade/Área informada não corresponde à Sub-OS.'
      });
    }
    if (req.unitAreaId && !unitAreas.some(a => a.id === req.unitAreaId && a.projectId === projectId)) {
      throw new AppError(ErrorCode.Validation, 'RDO unit/project mismatch', {
        safeMessage: 'A Unidade/Área não pertence ao Projeto selecionado.'
      });
    }
  }

  async getRdo(context: RequestContext, rdoId: RdoId): Promise<Rdo | null> {
    return this.rdoRepo.findById(context, rdoId);
  }

  /**
   * Verifica se o contexto autenticado possui autorização para criar/editar RDO para o profissional informado.
   * - DEVELOPER / ADMIN / ENGINEER: podem apontar para qualquer profissional autorizado.
   * - INSPECTOR / DOCUMENT_TECH / VIEWER: podem apontar SOMENTE para o seu próprio context.professionalId.
   */
  private assertCanCreateRdoForProfessional(context: RequestContext, targetProfessionalId: string): void {
    const canCreateForOthers =
      !context?.permissions ||
      context.permissions.has?.(PermissionCode.AdministrationManage) ||
      context.permissions.has?.(PermissionCode.ProjectManage) ||
      context.permissions.has?.(PermissionCode.EngineeringAccess) ||
      context.permissions.has?.(PermissionCode.IdentityManage);

    if (canCreateForOthers) return;

    if (context.professionalId && String(targetProfessionalId) !== String(context.professionalId)) {
      throw new AppError(ErrorCode.Authorization, "Você só possui autorização para gerar ou editar RDO para o seu próprio profissional.", {
        safeMessage: "Você só possui autorização para gerar ou editar RDO para o seu próprio profissional."
      });
    }
  }

  async listRdos(context: RequestContext, filters: RdoFilterOptions = {}): Promise<readonly Rdo[]> {
    const canViewOthers =
      !context?.permissions ||
      context.permissions.has?.(PermissionCode.AdministrationManage) ||
      context.permissions.has?.(PermissionCode.ProjectManage) ||
      context.permissions.has?.(PermissionCode.EngineeringAccess) ||
      context.permissions.has?.(PermissionCode.DocumentRead) ||
      context.permissions.has?.(PermissionCode.ReportReadAll);

    if (!canViewOthers && context.professionalId) {
      filters = { ...filters, professionalId: String(context.professionalId) };
    }
    return this.rdoRepo.find(context, filters);
  }

  async createRdo(context: RequestContext, req: CreateRdoRequest): Promise<Rdo> {
    const clientId = (req.clientId || context.activeClientId) as ClientId;
    if (!clientId) {
      throw new AppError(ErrorCode.Validation, 'Cliente é obrigatório para registrar o RDO');
    }

    if (!req.professionalId) throw new AppError(ErrorCode.Validation, 'Colaborador é obrigatório');
    this.assertCanCreateRdoForProfessional(context, req.professionalId);
    if (!req.date) throw new AppError(ErrorCode.Validation, 'Data do apontamento é obrigatória');

    // A Frente de Serviço define a que documento consolidado o apontamento
    // pertence — sem ela o RDO não tem onde ser consolidado.
    if (!req.serviceFrontId) {
      throw new AppError(ErrorCode.Validation, 'Service front is required for an RDO entry', {
        safeMessage: 'Selecione a Frente de Serviço do apontamento.'
      });
    }

    // Os IDs enviados pelo frontend NÃO podem sobrepor a hierarquia oficial.
    const validationContext = { ...context, activeClientId: clientId };
    const subOsContext = await this.requireSubOsContext(validationContext, req.professionalId, req.subWorkOrderId);
    const workOrderId = (subOsContext?.workOrderId || req.workOrderId) as string;
    if (!workOrderId) throw new AppError(ErrorCode.Validation, 'Ordem de Serviço (OS) é obrigatória');
    await this.assertRdoOperationalHierarchy(validationContext, clientId, req, subOsContext);

    validateRdoHours(req.startTime, req.endTime, req.normalHours, req.overtimeHours);

    // Anti-duplication check: avoid double entry for same professional, date, and shift
    const existing = await this.rdoRepo.find(context, {
      professionalId: req.professionalId,
      startDate: req.date,
      endDate: req.date,
      shift: req.shift || 'ADMINISTRATIVO'
    });

    if (existing.some(r => r.workOrderId === workOrderId && r.serviceFrontId === (req.serviceFrontId ? req.serviceFrontId as ServiceFrontId : null) && r.status !== 'CANCELLED')) {
      throw new AppError(ErrorCode.Conflict, `Já existe um apontamento registrado para este colaborador nesta data e turno.`);
    }

    // Resolve official function name strictly from professional registration
    // Função/Cargo vem do cadastro do Professional. Sem função cadastrada o
    // campo fica vazio — não se inventa um cargo nem se usa o papel de acesso.
    let functionName = (req.functionName || '').trim();
    if (this.identityRepo) {
      const prof = await this.identityRepo.getProfessional(req.professionalId as ProfessionalId);
      if (prof?.functionTitle) {
        functionName = prof.functionTitle.trim();
      }
    }

    // A autorização da Sub-OS já foi validada em requireSubOsContext, que
    // confere o vínculo com o COLABORADOR — regra mais estrita que a antiga,
    // que só conferia se a Sub-OS pertencia à OS enviada pela tela.

    const rdoId = randomUUID();
    const now = new Date().toISOString() as UtcDateTime;

    // O Projeto do apontamento vem da Frente escolhida, não de um campo solto.
    const projectId = req.projectId || (await this.resolveProjectIdFromFront(clientId, req.serviceFrontId));

    // Sequência do apontamento dentro do dia, para a ordem do consolidado.
    const sameDay = await this.rdoRepo.find(context, {
      professionalId: req.professionalId,
      startDate: req.date,
      endDate: req.date
    });
    const sequence = sameDay.filter(r => r.serviceFrontId === req.serviceFrontId).length + 1;

    const rdo: Rdo = {
      id: rdoId as RdoId,
      clientId,
      // OS, Projeto e Unidade/Área vêm da Sub-OS, não de seleção manual.
      workOrderId: workOrderId as WorkOrderId,
      subWorkOrderId: req.subWorkOrderId || null,
      serviceFrontId: req.serviceFrontId as ServiceFrontId,
      projectId: subOsContext?.projectId ?? projectId,
      unitAreaId: subOsContext?.unitAreaId ?? req.unitAreaId ?? null,
      sequence,
      inspectorComment: req.inspectorComment?.trim() || null,
      professionalId: req.professionalId as ProfessionalId,
      date: req.date,
      startTime: req.startTime || '07:30',
      endTime: req.endTime || '17:18',
      normalHours: req.normalHours ?? 8.8,
      overtimeHours: req.overtimeHours ?? 0,
      om: req.om ? req.om.trim() : null,
      functionName,
      shift: req.shift || 'ADMINISTRATIVO',
      description: (req.description || '').trim(),
      status: 'DRAFT',
      version: 1,
      createdBy: context.userId || null,
      updatedBy: context.userId || null,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      deletedBy: null,
      deletionReason: null
    };

    await this.rdoRepo.save(context, rdo);

    if (this.audit) {
      await this.audit.record(context, {
        action: "CREATE" as any,
        entityType: "Rdo",
        entityId: rdo.id,
        previousState: null,
        newState: { date: rdo.date, professionalId: rdo.professionalId, normalHours: rdo.normalHours },
        metadata: { action: "RDO_CREATED" }
      }).catch(() => {});
    }

    return rdo;
  }

  /**
   * Se o apontamento pode ser editado agora, e por que não, quando for o caso.
   * A tela usa isto para habilitar o botão Editar com a mensagem correta.
   */
  async getEntryEditability(context: RequestContext, rdoId: RdoId): Promise<EntryEditability> {
    const entry = await this.rdoRepo.findById(context, rdoId);
    const consolidations = entry ? await this.rdoRepo.listAllRevisions(context, rdoId) : [];
    return resolveEntryEditability({ entry, consolidations: consolidations as any });
  }

  /**
   * Rastro do apontamento com o que é realmente persistido: criação, última
   * edição e versão. Não há invenção de um histórico campo a campo que o
   * repositório não guarda.
   */
  async getEntryTrail(context: RequestContext, rdoId: RdoId): Promise<{
    createdAt: string; createdBy: string | null;
    updatedAt: string; updatedBy: string | null;
    version: number; consolidatedIn: { id: string; status: string | null }[];
    changeHistory: RdoEntryChangeRecord[];
  } | null> {
    const entry = await this.rdoRepo.findById(context, rdoId);
    if (!entry) return null;

    const consolidations = await this.rdoRepo.listAllRevisions(context, rdoId);
    return {
      createdAt: entry.createdAt,
      createdBy: entry.createdBy ?? null,
      updatedAt: entry.updatedAt,
      updatedBy: entry.updatedBy ?? null,
      version: entry.version,
      consolidatedIn: consolidations.map(c => ({ id: c.id, status: c.status ?? null })),
      // Cada edição, com o que mudou e de quê para quê.
      changeHistory: [...(entry.changeHistory ?? [])]
    };
  }

  async updateRdo(context: RequestContext, rdoId: RdoId, req: UpdateRdoRequest): Promise<Rdo> {
    // Editar apontamento é permissão própria, resolvida no catálogo central.
    new RbacAuthorizationService().require(context, PermissionCode.RdoUpdate);

    const existing = await this.rdoRepo.findById(context, rdoId);
    if (!existing) throw new AppError(ErrorCode.NotFound, 'Apontamento RDO não encontrado');

    this.assertCanCreateRdoForProfessional(context, existing.professionalId);
    if (req.professionalId) {
      this.assertCanCreateRdoForProfessional(context, req.professionalId);
    }

    // Apontamento congelado por consolidado aprovado/assinado não muda em
    // silêncio: a correção passa a exigir revisão documental.
    const consolidations = await this.rdoRepo.listAllRevisions(context, rdoId);
    const editability = resolveEntryEditability({ entry: existing, consolidations: consolidations as any });
    if (!editability.editable) {
      throw new AppError(ErrorCode.Conflict, `RDO entry ${rdoId} is frozen`, {
        safeMessage: editability.reason ?? ENTRY_FROZEN_MESSAGE
      });
    }

    validateRdoHours(req.startTime, req.endTime, req.normalHours, req.overtimeHours);

    const clientId = (req.clientId || existing.clientId || context.activeClientId) as ClientId;

    // A autorização da Sub-OS já foi validada em requireSubOsContext, que
    // confere o vínculo com o COLABORADOR — regra mais estrita que a antiga,
    // que só conferia se a Sub-OS pertencia à OS enviada pela tela.

    let functionName = existing.functionName;
    if (this.identityRepo) {
      const prof = await this.identityRepo.getProfessional(req.professionalId as ProfessionalId);
      if (prof?.functionTitle) {
        functionName = prof.functionTitle.trim();
      }
    }

    // A Frente continua obrigatória na edição — um apontamento não pode perder
    // o vínculo que o coloca num RDO Consolidado.
    const serviceFrontId = (req.serviceFrontId || existing.serviceFrontId) as ServiceFrontId | null;
    if (!serviceFrontId) {
      throw new AppError(ErrorCode.Validation, 'Service front is required for an RDO entry', {
        safeMessage: 'Selecione a Frente de Serviço do apontamento.'
      });
    }

    // A Sub-OS informada precisa pertencer ao colaborador informado, e é dela
    // que OS, Projeto e Unidade/Área são re-resolvidos. Registro legado sem
    // Sub-OS só passa por aqui quando a edição já informa uma.
    const subWorkOrderId = req.subWorkOrderId ?? existing.subWorkOrderId ?? null;
    const validationContext = { ...context, activeClientId: clientId };
    const subOsContext = subWorkOrderId
      ? await this.requireSubOsContext(validationContext, req.professionalId, subWorkOrderId)
      : null;
    // Registros legados sem Sub-OS podem continuar em leitura; novas combinações
    // enviadas na edição precisam obedecer à mesma regra da criação.
    if (subOsContext) {
      await this.assertRdoOperationalHierarchy(validationContext, clientId, {
        workOrderId: req.workOrderId || existing.workOrderId,
        projectId: req.projectId ?? existing.projectId ?? null,
        serviceFrontId: req.serviceFrontId || existing.serviceFrontId,
        unitAreaId: req.unitAreaId ?? existing.unitAreaId ?? null
      }, subOsContext);
    }

    // Projeto acompanha a Sub-OS; sem ela, a Frente continua resolvendo.
    const projectId = subOsContext?.projectId
      ?? req.projectId
      ?? (serviceFrontId !== existing.serviceFrontId
        ? await this.resolveProjectIdFromFront(clientId, serviceFrontId)
        : existing.projectId ?? await this.resolveProjectIdFromFront(clientId, serviceFrontId));

    const now = new Date().toISOString() as UtcDateTime;
    const updated: Rdo = {
      ...existing,
      // OS e Unidade/Área acompanham a Sub-OS resolvida.
      workOrderId: (subOsContext?.workOrderId ?? req.workOrderId ?? existing.workOrderId) as WorkOrderId,
      subWorkOrderId: subWorkOrderId,
      unitAreaId: subOsContext?.unitAreaId ?? req.unitAreaId ?? existing.unitAreaId ?? null,
      serviceFrontId,
      projectId,
      inspectorComment: req.inspectorComment !== undefined ? (req.inspectorComment?.trim() || null) : existing.inspectorComment ?? null,
      professionalId: req.professionalId as ProfessionalId,
      date: req.date,
      startTime: req.startTime,
      endTime: req.endTime,
      normalHours: req.normalHours,
      overtimeHours: req.overtimeHours,
      om: req.om ? req.om.trim() : null,
      functionName,
      shift: req.shift || existing.shift,
      description: req.description !== undefined ? req.description.trim() : existing.description,
      status: req.status || existing.status,
      // UPDATE no registro existente: id, createdAt e createdBy são preservados.
      version: existing.version + 1,
      createdBy: existing.createdBy ?? null,
      updatedBy: context.userId || null,
      updatedAt: now
    };

    const changes = diffRdoEntry(existing as any, updated as any);

    // O histórico fica no próprio apontamento: é o que sobrevive ao recorder
    // de auditoria que este app descarta. Só grava quando algo mudou de fato.
    const withHistory: Rdo = changes.length === 0 ? updated : {
      ...updated,
      changeHistory: appendEntryChangeHistory(existing.changeHistory, {
        changedAt: now,
        changedByUserId: context.userId || null,
        changedByProfessionalId: context.professionalId ?? null,
        version: updated.version,
        changes
      })
    };

    await this.rdoRepo.save(context, withHistory);

    if (this.audit) {
      await this.audit.record(context, {
        action: "UPDATE" as any,
        entityType: "Rdo",
        entityId: rdoId,
        // Estado anterior e novo de cada campo alterado — é isso que dá
        // rastreabilidade à edição, em vez de só "algo mudou".
        previousState: Object.fromEntries(changes.map(c => [c.field, c.previousValue])) as any,
        newState: Object.fromEntries(changes.map(c => [c.field, c.newValue])) as any,
        metadata: {
          action: "RDO_ENTRY_UPDATED",
          rdoEntryId: rdoId,
          alteradoPor: context.userId || null,
          professionalId: context.professionalId ?? null,
          dataHora: now,
          camposAlterados: changes.map(c => c.field),
          version: updated.version
        }
      }).catch(() => {});
    }

    return withHistory;
  }

  async deleteRdo(context: RequestContext, rdoId: RdoId, reason?: string): Promise<void> {
    const existing = await this.rdoRepo.findById(context, rdoId);
    if (!existing) throw new AppError(ErrorCode.NotFound, 'RDO não encontrado');
    if (existing.status === 'ISSUED') {
      throw new AppError(ErrorCode.Conflict, 'Não é possível excluir um RDO já emitido');
    }

    await this.rdoRepo.delete(context, rdoId, reason);

    if (this.audit) {
      await this.audit.record(context, {
        action: "DELETE" as any,
        entityType: "Rdo",
        entityId: rdoId,
        previousState: { status: existing.status },
        newState: { status: 'CANCELLED' },
        metadata: { action: "RDO_CANCELLED", reason: reason ?? null }
      }).catch(() => {});
    }
  }

  async getEnrichedRdos(context: RequestContext, filters: RdoFilterOptions = {}): Promise<readonly EnrichedRdo[]> {
    const rdos = await this.rdoRepo.find(context, filters);

    // Os cadastros são consultados pelo cliente DOS PRÓPRIOS apontamentos, não
    // pelo cliente do contexto: quando a sessão não tem cliente ativo, a
    // apresentação envia o sentinela 'cli-default', e consultar por ele
    // devolvia listas vazias — era assim que colaborador, OS e Sub-OS
    // apareciam como id bruto na tabela.
    const entryClientIds = Array.from(new Set(rdos.map(r => String(r.clientId)).filter(Boolean)));
    const lookupClientIds = entryClientIds.length > 0
      ? entryClientIds
      : (context.activeClientId ? [String(context.activeClientId)] : []);

    // Cache metadata lookups
    const clientNameMap = new Map<string, string>();
    const profMap = new Map<string, string>();
    const profTitleMap = new Map<string, string>();
    const woMap = new Map<string, string>();
    const subWoMap = new Map<string, string>();
    const frontMap = new Map<string, string>();
    const frontProjectMap = new Map<string, string>();
    const projectMap = new Map<string, string>();

    for (const lookupClientId of lookupClientIds) {
      if (this.identityRepo) {
        const client = await this.identityRepo.getClient(lookupClientId as any);
        if (client) clientNameMap.set(lookupClientId, client.name || client.legalName || 'Cliente');

        const profs = await this.identityRepo.listProfessionals(lookupClientId as any);
        for (const p of profs) {
          profMap.set(p.id, p.name);
          if (p.functionTitle) profTitleMap.set(p.id, p.functionTitle);
        }
      }

      if (this.adminRepo) {
        const wos = await this.adminRepo.getWorkOrders(lookupClientId as any);
        for (const wo of wos) woMap.set(wo.id, wo.code);

        const swos = await this.adminRepo.getSubWorkOrders(lookupClientId as any);
        for (const swo of swos) subWoMap.set(swo.id, swo.code);

        const fronts = await this.adminRepo.getServiceFronts(lookupClientId as any);
        for (const sf of fronts) {
          frontMap.set(sf.id, sf.code ? `${sf.code} — ${sf.name}` : sf.name);
          // O Projeto do apontamento vem da Frente: registros antigos, gravados
          // antes do campo projectId, ainda são resolvidos por ela.
          if (sf.projectId) frontProjectMap.set(sf.id, sf.projectId);
        }
      }
    }

    // Nome do Projeto para a listagem — o vínculo é o mesmo usado no documento.
    if (this.projectLookup) {
      const projectIds = new Set<string>();
      for (const r of rdos) {
        const projectId = r.projectId || (r.serviceFrontId ? frontProjectMap.get(r.serviceFrontId) : null);
        if (projectId) projectIds.add(projectId);
      }
      for (const projectId of projectIds) {
        const project = await this.projectLookup(projectId);
        if (project) projectMap.set(projectId, `${project.code} — ${project.name}`);
      }
    }

    return rdos.map(r => {
      // Nenhum id interno chega à tabela. Quando o cadastro correspondente não
      // existe mais, o registro histórico é identificado como tal — e continua
      // editável, para o usuário escolher um cadastro válido.
      const professionalName = profMap.get(r.professionalId) || UNRESOLVED_PROFESSIONAL;
      // Cadastro atual do Professional primeiro; o valor congelado no
      // apontamento é a queda para um profissional que já não existe.
      const functionName = profTitleMap.get(r.professionalId) || r.functionName || '';
      const workOrderCode = woMap.get(r.workOrderId) || UNRESOLVED_LABEL;
      const subWorkOrderCode = r.subWorkOrderId ? (subWoMap.get(r.subWorkOrderId) || UNRESOLVED_LABEL) : '-';
      const serviceFrontName = r.serviceFrontId ? (frontMap.get(r.serviceFrontId) || UNRESOLVED_LABEL) : '-';
      const clientName = clientNameMap.get(r.clientId) || 'CLIENTE';
      const projectId = r.projectId || (r.serviceFrontId ? frontProjectMap.get(r.serviceFrontId) : null);
      const projectName = projectId ? (projectMap.get(projectId) || UNRESOLVED_LABEL) : '-';

      return {
        ...r,
        // Registro antigo sem projectId gravado passa a carregar o Projeto da
        // sua Frente, para filtrar e listar junto com os novos.
        projectId: projectId ?? null,
        functionName,
        clientName,
        professionalName,
        workOrderCode,
        subWorkOrderCode,
        serviceFrontName,
        projectName
      };
    });
  }

  /**
   * Groups RDOs strictly according to official rule:
   * 1. Group by COLABORADOR (Alphabetically ASC)
   * 2. Within each collaborator: Sort by DATA (Chronologically ASC)
   */
  async getCollaboratorGroups(context: RequestContext, filters: RdoFilterOptions = {}): Promise<CollaboratorRdoGroup[]> {
    const enriched = await this.getEnrichedRdos(context, filters);

    const groupMap = new Map<string, {
      professionalId: string;
      professionalName: string;
      functionName: string;
      rdos: EnrichedRdo[];
    }>();

    for (const r of enriched) {
      const key = r.professionalName || r.professionalId;
      let grp = groupMap.get(key);
      if (!grp) {
        grp = {
          professionalId: r.professionalId,
          professionalName: r.professionalName || 'COLABORADOR',
          functionName: r.functionName,
          rdos: []
        };
        groupMap.set(key, grp);
      }
      grp.rdos.push(r);
    }

    // Sort collaborator groups alphabetically by name ASC
    const sortedGroups = Array.from(groupMap.values()).sort((a, b) =>
      a.professionalName.localeCompare(b.professionalName, 'pt-BR', { sensitivity: 'base' })
    );

    return sortedGroups.map(grp => {
      // Sort RDOs within this group chronologically by date ASC
      const sortedRdos = [...grp.rdos].sort((a, b) => a.date.localeCompare(b.date));

      let totalNormalHours = 0;
      let totalOvertimeHours = 0;
      const uniqueDates = new Set<string>();

      for (const r of sortedRdos) {
        totalNormalHours += r.normalHours;
        totalOvertimeHours += r.overtimeHours;
        uniqueDates.add(r.date);
      }

      const datesArray = Array.from(uniqueDates).sort();
      const periodStart = datesArray[0] || '';
      const periodEnd = datesArray[datesArray.length - 1] || '';

      return {
        professionalId: grp.professionalId,
        professionalName: grp.professionalName,
        functionName: grp.functionName,
        rdos: sortedRdos,
        totalDays: uniqueDates.size,
        totalNormalHours: Number(totalNormalHours.toFixed(2)),
        totalOvertimeHours: Number(totalOvertimeHours.toFixed(2)),
        periodStart,
        periodEnd
      };
    });
  }

  async consolidateRdos(
    context: RequestContext,
    req: {
      rdoIds: string[];
      serviceFrontId?: string | null;
      workOrderId?: string | null;
      subWorkOrderId?: string | null;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<RdoConsolidated> {
    const clientId = this.getClientId(context);

    if (!req.rdoIds || req.rdoIds.length === 0) {
      throw new AppError(ErrorCode.Validation, 'Nenhum RDO selecionado para consolidação');
    }

    const allRdos = await Promise.all(req.rdoIds.map(id => this.rdoRepo.findById(context, id as RdoId)));
    const validRdos = allRdos.filter((r): r is Rdo => !!r && r.clientId === clientId && !r.deletedAt && r.status !== 'CANCELLED');

    if (validRdos.length === 0) {
      throw new AppError(ErrorCode.Validation, 'Nenhum RDO válido encontrado para consolidar');
    }

    let totalNormal = 0;
    let totalOvertime = 0;
    const dates = new Set<string>();
    const collaborators = new Set<string>();

    for (const r of validRdos) {
      totalNormal += r.normalHours;
      totalOvertime += r.overtimeHours;
      dates.add(r.date);
      collaborators.add(r.professionalId);
    }

    const sortedDates = Array.from(dates).sort();
    const periodStart = req.startDate || sortedDates[0] || new Date().toISOString().substring(0, 10);
    const periodEnd = req.endDate || sortedDates[sortedDates.length - 1] || periodStart;

    const docNumber = `RDO-CONS-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
    const now = new Date().toISOString() as UtcDateTime;

    const consolidated: RdoConsolidated = {
      id: randomUUID() as RdoConsolidatedId,
      clientId,
      periodStart,
      periodEnd,
      workOrderId: req.workOrderId ? (req.workOrderId as WorkOrderId) : (validRdos[0]?.workOrderId || null),
      subWorkOrderId: req.subWorkOrderId || (validRdos[0]?.subWorkOrderId || null),
      serviceFrontId: req.serviceFrontId ? (req.serviceFrontId as ServiceFrontId) : (validRdos[0]?.serviceFrontId || null),
      rdoIds: validRdos.map(r => r.id),
      totalNormalHours: Number(totalNormal.toFixed(2)),
      totalOvertimeHours: Number(totalOvertime.toFixed(2)),
      totalDays: dates.size,
      collaboratorCount: collaborators.size,
      generatedAt: now,
      generatedBy: context.userId || 'Sistema',
      documentNumber: docNumber,
      documentRevision: '00',
      status: 'ISSUED',
      version: 1,
      createdAt: now,
      updatedAt: now
    };

    await this.rdoRepo.saveConsolidated(context, consolidated);

    if (this.audit) {
      await this.audit.record(context, {
        action: "CREATE" as any,
        entityType: "RdoConsolidated",
        entityId: consolidated.id,
        previousState: null,
        newState: {
          periodStart: consolidated.periodStart,
          periodEnd: consolidated.periodEnd,
          rdoCount: consolidated.rdoIds.length,
          totalNormalHours: consolidated.totalNormalHours,
          totalOvertimeHours: consolidated.totalOvertimeHours
        },
        metadata: { action: "RDO_CONSOLIDATED_GENERATED" }
      }).catch(() => {});
    }

    return consolidated;
  }

  async buildIndividualDocumentSnapshot(context: RequestContext, rdoId: RdoId): Promise<DocumentSnapshot> {
    const enrichedList = await this.getEnrichedRdos(context, {});
    const rdo = enrichedList.find(r => r.id === rdoId);
    if (!rdo) throw new AppError(ErrorCode.NotFound, 'RDO não encontrado para geração de documento');

    const snapshot: DocumentSnapshot = {
      id: rdo.id,
      type: 'RDO',
      revision: String(rdo.version || 1),
      createdAt: new Date(rdo.createdAt),
      clientId: rdo.clientId,
      documentNumber: `RDO-${rdo.date.replace(/-/g, '')}-${rdo.id.substring(rdo.id.length - 4).toUpperCase()}`,
      data: {
        rdo,
        clientName: rdo.clientName || 'CLIENTE',
        workOrderCode: rdo.workOrderCode || '-',
        subWorkOrderCode: rdo.subWorkOrderCode || '-',
        serviceFrontName: rdo.serviceFrontName || '-',
        professionalName: rdo.professionalName || 'COLABORADOR',
        emissionDate: new Date().toLocaleDateString('pt-BR')
      }
    };

    return snapshot;
  }


  /**
   * RDO CONSOLIDADO — 1 Projeto + 1 Frente de Serviço + 1 período.
   *
   * Reúne os apontamentos que já existem; não funde textos, não resume e não
   * reescreve descrição nenhuma. Devolve o MODELO do documento, que é o mesmo
   * objeto usado pelo preview e pelo PDF — daí a ordem ser necessariamente
   * idêntica nos dois.
   */
  /**
   * O que existe para o Projeto + Frente + período, antes de gerar: quantos
   * apontamentos, e quais colaboradores realmente têm registro ali.
   *
   * É a fonte do contador do modal e do seletor do modo UM COLABORADOR — que
   * só oferece quem de fato aparece no documento.
   */
  async previewConsolidationScope(
    context: RequestContext,
    filters: Omit<RdoConsolidationFilters, "collaboratorScope" | "professionalId">
  ): Promise<{
    entryCount: number;
    collaborators: { professionalId: string; name: string; functionName: string; entryCount: number }[];
  }> {
    if (!filters.projectId || !filters.serviceFrontId || !filters.startDate || !filters.endDate) {
      return { entryCount: 0, collaborators: [] };
    }
    if (filters.endDate < filters.startDate) {
      return { entryCount: 0, collaborators: [] };
    }

    const candidates = await this.getEnrichedRdos(context, {
      serviceFrontId: filters.serviceFrontId,
      startDate: filters.startDate,
      endDate: filters.endDate
    });
    // Escopo TODOS: é a lista completa do período/Frente.
    const entries = filterEntriesForConsolidation(candidates, { ...filters, collaboratorScope: "ALL" });

    const byProfessional = new Map<string, { professionalId: string; name: string; functionName: string; entryCount: number }>();
    for (const entry of entries) {
      const existing = byProfessional.get(entry.professionalId);
      if (existing) {
        existing.entryCount += 1;
      } else {
        byProfessional.set(entry.professionalId, {
          professionalId: entry.professionalId,
          name: entry.professionalName || entry.professionalId,
          functionName: entry.functionName || "",
          entryCount: 1
        });
      }
    }

    return {
      entryCount: entries.length,
      collaborators: Array.from(byProfessional.values())
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }))
    };
  }

  async buildConsolidatedModel(
    context: RequestContext,
    filters: RdoConsolidationFilters
  ): Promise<RdoConsolidatedModel> {
    validateConsolidationFilters(filters);

    const clientId = this.getClientId(context);

    // A busca já vai filtrada ao repositório; o filtro puro roda por cima para
    // garantir a regra (Frente, período, colaboradores, sem duplicatas).
    const candidates = await this.getEnrichedRdos(context, {
      serviceFrontId: filters.serviceFrontId,
      startDate: filters.startDate,
      endDate: filters.endDate
    });
    const entries = filterEntriesForConsolidation(candidates, filters);

    if (entries.length === 0) {
      // Nada encontrado é um beco sem saída se a tela não disser o que existe:
      // o usuário não sabe se errou o período, a Frente, ou se realmente não há
      // apontamento. O diagnóstico abaixo consulta os mesmos dados, afrouxando
      // um filtro por vez.
      const diagnosis = await this.diagnoseEmptyConsolidation(context, filters);
      throw new AppError(ErrorCode.NotFound, 'No RDO entries for the selected front and period', {
        safeMessage: `${NO_ENTRIES_MESSAGE}${diagnosis}`
      });
    }

    const header = await this.buildConsolidatedHeader(context, clientId, filters, entries[0]);
    return buildConsolidatedModel(entries, header);
  }

  /**
   * Por que o resultado veio vazio: apontamentos da Frente fora do período,
   * apontamentos no período em outra Frente, ou apontamentos sem Frente
   * nenhuma (registros antigos, gravados antes de a Frente ser obrigatória).
   */
  private async diagnoseEmptyConsolidation(
    context: RequestContext,
    filters: RdoConsolidationFilters
  ): Promise<string> {
    const all = await this.rdoRepo.find(context, {});
    const alive = all.filter(r => !r.deletedAt && r.status !== 'CANCELLED');

    const naFrente = alive.filter(r => r.serviceFrontId === filters.serviceFrontId);
    const noPeriodo = alive.filter(r => r.date >= filters.startDate && r.date <= filters.endDate);
    const semFrente = noPeriodo.filter(r => !r.serviceFrontId);

    const pistas: string[] = [];

    if (naFrente.length > 0) {
      const datas = naFrente.map(r => r.date).sort();
      pistas.push(
        `Esta Frente possui ${naFrente.length} apontamento(s), entre ${formatDateBr(datas[0]!)} e ${formatDateBr(datas[datas.length - 1]!)} — fora do período informado.`
      );
    }

    if (noPeriodo.length > 0) {
      const outrasFrentes = new Set(noPeriodo.filter(r => r.serviceFrontId && r.serviceFrontId !== filters.serviceFrontId).map(r => r.serviceFrontId));
      if (outrasFrentes.size > 0) {
        pistas.push(`No período há ${noPeriodo.length} apontamento(s) em ${outrasFrentes.size} outra(s) Frente(s).`);
      }
      if (semFrente.length > 0) {
        pistas.push(`${semFrente.length} apontamento(s) do período não têm Frente de Serviço definida e por isso não entram em nenhum consolidado — edite-os e informe a Frente.`);
      }
    }

    if (naFrente.length === 0 && noPeriodo.length === 0) {
      pistas.push('Não há apontamentos de RDO registrados nesta Frente nem neste período.');
    }

    return pistas.length > 0 ? ` ${pistas.join(' ')}` : '';
  }

  private async buildConsolidatedHeader(
    context: RequestContext,
    clientId: ClientId,
    filters: RdoConsolidationFilters,
    sample: EnrichedRdo | undefined
  ): Promise<RdoConsolidatedHeader> {
    let clientName = sample?.clientName || 'CLIENTE';
    let clientDocument = EMPTY_FIELD;
    let clientAcronym = EMPTY_FIELD;

    if (this.identityRepo) {
      const client = await this.identityRepo.getClient(clientId);
      if (client) {
        clientName = client.name || client.legalName || clientName;
        clientDocument = client.document || EMPTY_FIELD;
        clientAcronym = client.code || EMPTY_FIELD;
      }
    }

    let serviceFrontName = sample?.serviceFrontName || EMPTY_FIELD;
    let projectName = EMPTY_FIELD;

    if (this.adminRepo) {
      const fronts = await this.adminRepo.getServiceFronts(clientId);
      const front = fronts.find(f => f.id === filters.serviceFrontId);
      if (front) {
        // O nome cadastrado da Frente, nunca o id interno.
        serviceFrontName = front.code ? `${front.code} — ${front.name}` : front.name;
      }
    }

    if (this.projectLookup) {
      const project = await this.projectLookup(filters.projectId);
      if (project) projectName = project.code ? `${project.code} — ${project.name}` : project.name;
    }

    return {
      emissionDate: new Date().toISOString().substring(0, 10),
      projectName,
      serviceFrontName,
      periodStart: filters.startDate,
      periodEnd: filters.endDate,
      clientName,
      clientDocument,
      clientAcronym,
      documentNumber: this.buildConsolidatedNumber(filters),
      documentRevision: '0'
    };
  }

  /**
   * Número determinístico: a mesma Frente e o mesmo período devolvem sempre o
   * mesmo número, para o documento não trocar de identidade a cada reimpressão.
   */
  private buildConsolidatedNumber(filters: RdoConsolidationFilters): string {
    const front = filters.serviceFrontId.replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase();
    const start = filters.startDate.replace(/-/g, '');
    const end = filters.endDate.replace(/-/g, '');
    return `RDO-CONS-${front}-${start}-${end}`;
  }

  /** Snapshot do documento — envolve o MESMO modelo que o preview consome. */
  async buildConsolidatedSnapshotFromFilters(
    context: RequestContext,
    filters: RdoConsolidationFilters
  ): Promise<DocumentSnapshot> {
    const model = await this.buildConsolidatedModel(context, filters);
    const single = filters.collaboratorScope === 'ONE' ? model.collaborators[0]?.name ?? null : null;

    return {
      id: model.header.documentNumber,
      type: 'RDO_CONSOLIDATED',
      revision: model.header.documentRevision,
      createdAt: new Date(),
      documentNumber: model.header.documentNumber,
      clientId: this.getClientId(context),
      data: {
        model,
        fileName: buildConsolidatedFileName({
          serviceFrontName: model.header.serviceFrontName,
          periodStart: model.header.periodStart,
          periodEnd: model.header.periodEnd,
          collaboratorName: single
        })
      }
    };
  }
}
